// client/src/components/admin/TaskManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateTaskMutation,
  useGetAssignedTasksQuery,
  useVerifyTaskMutation,
  useGetFieldEmployeesQuery,
} from "../../redux/admin/taskApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import { useStatus } from "../../popup/NotificationContext";

// ✅ MUI
import {
  Box,
  Paper,
  Stack,
  Typography,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  InputAdornment,
  Tooltip,
  Grid,
  FormControlLabel,
  Switch,
  Autocomplete,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ScrollableTableContainer from "../common/ScrollableTableContainer";

// lucide
import { RefreshCw, X, MapPin, Search } from "lucide-react";
import { useListItemsQuery, useListItemUnitsQuery } from "../../redux/admin/inventoryApi";

const TASK_TYPE_OPTIONS = [
  {
    value: "device_install",
    label: "Device Install",
    helper: "Use this for new GPS/device installation tasks",
  },
  {
    value: "gps_repair",
    label: "GPS Repair",
    helper: "Troubleshooting and repair visit",
  },
  {
    value: "sim_change",
    label: "SIM Change",
    helper: "SIM replacement or network issue task",
  },
  {
    value: "device_replace",
    label: "Device Replace",
    helper: "Replace old or faulty hardware",
  },
  {
    value: "site_visit",
    label: "Site Visit",
    helper: "Inspection, diagnosis, or on-site check",
  },
  {
    value: "device_update",
    label: "Device Update",
    helper: "Update old device details using selected inventory values",
  },
  {
    value: "other",
    label: "Other",
    helper: "Any task outside the standard categories",
  },
];

const HARDWARE_TASK_TYPES = new Set([
  "gps_install",
  "device_install",
  "gps_repair",
  "sim_change",
  "device_replace",
  "device_update",
]);

const INSTALL_TASK_TYPES = new Set(["gps_install", "device_install"]);
const UPDATE_TASK_TYPES = new Set(["gps_repair", "sim_change", "device_replace", "device_update"]);
const NO_INVENTORY_TASK_TYPES = new Set(["site_visit", "other"]);

const DEFAULT_SELECTIONS = [
  {
    slotKey: "gps",
    slotLabel: "GPS Inventory",
    category: "gps",
    itemId: "",
    unitId: "",
    serial: "",
    qty: 1,
    handover: { ownerType: "vehicle", ownerId: "", ownerLabel: "" },
  },
  {
    slotKey: "sim",
    slotLabel: "SIM Inventory",
    category: "sim",
    itemId: "",
    unitId: "",
    serial: "",
    qty: 1,
    handover: { ownerType: "vehicle", ownerId: "", ownerLabel: "" },
  },
  {
    slotKey: "bulk_inventory",
    slotLabel: "Other Inventory / Bulk Item",
    category: "bulk",
    itemId: "",
    unitId: "",
    serial: "",
    qty: 1,
    handover: { ownerType: "vehicle", ownerId: "", ownerLabel: "" },
  },
];

const buildDefaultFormData = () => ({
  taskType: "device_install",
  title: "",
  description: "",
  assignedTo: "",
  siteAddress: "",
  siteLatitude: "",
  siteLongitude: "",
  clientName: "",
  clientPhone: "",
  deviceImei: "",
  newDeviceImei: "",
  targetDeviceId: "",
  vehicleNumber: "",
  scheduledDate: "",
  scheduledTime: "",
  priority: "medium",
  inventoryRequired: true,
  inventorySelections: DEFAULT_SELECTIONS.map((selection) => ({
    ...selection,
    handover: { ...selection.handover },
  })),
});

const formatUnitOptionLabel = (unit = {}) => {
  const parts = [
    unit?.imei,
    unit?.serial && unit?.serial !== unit?.imei ? unit.serial : "",
    unit?.modelType,
    unit?.simNumber,
    unit?.imsiNumber,
    unit?.provider,
  ].filter(Boolean);
  return parts.join(" • ") || "Unnamed unit";
};

const getSelectionSnapshotRows = (selection = {}) => {
  const details = selection?.details || {};
  return [
    { label: "Inventory Item", value: selection?.itemName || "—" },
    { label: "Quantity", value: selection?.qty ? String(selection.qty) : "—" },
    { label: "Serial", value: selection?.serial || details?.serial || "—" },
    { label: "IMEI", value: details?.imei || "—" },
    { label: "Model Type", value: details?.modelType || "—" },
    { label: "SIM Number", value: details?.simNumber || "—" },
    { label: "IMSI", value: details?.imsiNumber || "—" },
    { label: "Provider", value: details?.provider || "—" },
    { label: "SIM Type", value: details?.simType || "—" },
    { label: "IoT SIM", value: details?.iotSimNumber || "—" },
  ].filter((row) => row.value && row.value !== "—" || row.label === "Inventory Item");
};

const getTaskInfoValue = (value, fallback = "—") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
};

const formatTaskDateOnly = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return getTaskInfoValue(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTaskDateTimeLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return getTaskInfoValue(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildTaskInfoRows = (task = {}) => {
  const snapshot = task.currentDeviceSnapshot || {};
  const assignedToName = task.assignedTo?.username || task.assignedTo?.name || task.assignedTo?.mobileNo || "—";
  const assignedByName = task.assignedBy?.username || task.assignedBy?.name || task.assignedBy?.mobileNo || "—";
  const verifiedByName = task.verifiedBy?.username || task.verifiedBy?.name || "—";
  const taskTypeLabel = getTaskInfoValue(task.taskType).replaceAll("_", " ");

  const taskSummaryRows = [
    { label: "Task Title", value: getTaskInfoValue(task.title) },
    { label: "Task Type", value: taskTypeLabel },
    { label: "Status", value: getTaskInfoValue(task.status).replaceAll("_", " ") },
    { label: "Verification", value: getTaskInfoValue(task.verificationStatus).replaceAll("_", " ") },
    { label: "Priority", value: getTaskInfoValue(task.priority) },
    { label: "Assigned To", value: assignedToName },
    { label: "Assigned By", value: assignedByName },
    { label: "Assigned At", value: formatTaskDateTimeLabel(task.assignedAt) },
    { label: "Started At", value: formatTaskDateTimeLabel(task.startedAt) },
    { label: "Completed At", value: formatTaskDateTimeLabel(task.completedAt) },
    { label: "Verified By", value: verifiedByName },
    { label: "Verified At", value: formatTaskDateTimeLabel(task.verifiedAt) },
  ];

  const vehicleInfoRows = [
    { label: "Vehicle Number", value: getTaskInfoValue(task.vehicleNumber || snapshot.vehicleNumber) },
    { label: "Vehicle Name", value: getTaskInfoValue(snapshot.name || task.vehicleName) },
    { label: "Target Device ID", value: getTaskInfoValue(task.targetDeviceId || snapshot.deviceId) },
    { label: "Current Device IMEI", value: getTaskInfoValue(task.deviceImei || snapshot.imei) },
    { label: "Updated / New IMEI", value: getTaskInfoValue(task.newDeviceImei) },
    { label: "Model", value: getTaskInfoValue(snapshot.model || task.registeredModelCandidate) },
    { label: "Protocol", value: getTaskInfoValue(snapshot.protocol || task.registeredProtocolCandidate) },
    { label: "Vehicle Type", value: getTaskInfoValue(snapshot.vehicleType) },
    { label: "License Expiry", value: formatTaskDateOnly(snapshot.licenseExpiryDate) },
  ];

  const simInfoRows = [
    { label: "SIM Number", value: getTaskInfoValue(task.simNumber || snapshot.msdn) },
    { label: "IMSI Number", value: getTaskInfoValue(task.imsiNumber || snapshot.imsi) },
    { label: "SIM Provider", value: getTaskInfoValue(task.simProvider) },
    { label: "SIM Type", value: getTaskInfoValue(task.simType) },
    { label: "IoT SIM Number", value: getTaskInfoValue(task.iotSimNumber) },
  ];

  const siteInfoRows = [
    { label: "Site Address", value: getTaskInfoValue(task.siteAddress) },
    { label: "Latitude", value: getTaskInfoValue(task.siteLatitude) },
    { label: "Longitude", value: getTaskInfoValue(task.siteLongitude) },
    { label: "Client Name", value: getTaskInfoValue(task.clientName) },
    { label: "Client Phone", value: getTaskInfoValue(task.clientPhone) },
    { label: "Scheduled Date", value: formatTaskDateOnly(task.scheduledDate) },
    { label: "Scheduled Time", value: getTaskInfoValue(task.scheduledTime) },
  ];

  const inventoryMetaRows = [
    { label: "Inventory Required", value: task.inventoryRequired ? "Yes" : "No" },
    { label: "Inventory Status", value: formatInventoryStatusLabel(task.inventoryStatus) },
    { label: "Issued At", value: formatTaskDateTimeLabel(task.issuedAt) },
    { label: "Handed Over At", value: formatTaskDateTimeLabel(task.handedOverAt) },
    { label: "Handover Owner", value: getTaskInfoValue(task.inventoryHandover?.ownerLabel) },
    { label: "Inventory Error", value: getTaskInfoValue(task.inventoryError) },
  ];

  const notesRows = [
    { label: "Description", value: getTaskInfoValue(task.description) },
    { label: "Field Notes", value: getTaskInfoValue(task.fieldNotes) },
    { label: "Verification Notes", value: getTaskInfoValue(task.verificationNotes) },
    { label: "Rejection Reason", value: getTaskInfoValue(task.rejectionReason) },
  ];

  return {
    taskSummaryRows,
    vehicleInfoRows,
    simInfoRows,
    siteInfoRows,
    inventoryMetaRows,
    notesRows,
  };
};

const InfoTableSection = ({ title, rows = [] }) => {
  const visibleRows = rows.filter((row) => row && row.label);
  if (!visibleRows.length) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
      <Typography sx={{ fontWeight: 900, mb: 1 }}>{title}</Typography>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {visibleRows.map((row, index) => (
          <Stack
            key={`${title}-${row.label}`}
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            sx={{
              px: 1.4,
              py: 1,
              borderTop: index === 0 ? "none" : "1px solid",
              borderColor: "divider",
              bgcolor: index % 2 ? "action.hover" : "transparent",
            }}
          >
            <Typography sx={{ fontWeight: 800, minWidth: { xs: "100%", md: 220 }, fontSize: 13 }}>
              {row.label}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {getTaskInfoValue(row.value)}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Paper>
  );
};

const getInventoryItemOptionLabel = (item = {}) =>
  item?.name
    ? `${item.name} • ${item.isSerialized ? "Serialized" : "Bulk"}`
    : "";

const getGpsUnitOptionLabel = (unit = {}) => formatUnitOptionLabel(unit);

const getSimUnitOptionLabel = (unit = {}) => {
  const primary = unit?.simNumber || unit?.serial || unit?.imei || "SIM unit";
  const secondary = [unit?.imsiNumber, unit?.provider, unit?.iotSimNumber].filter(Boolean).join(" • ");
  return secondary ? `${primary} • ${secondary}` : primary;
};

const formatInventoryStatusLabel = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (!normalized || normalized === "none") return "Required";
  return `Required • ${normalized.replaceAll("_", " ")}`;
};

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const TaskManagement = () => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { showStatus } = useStatus();

  const [activeTab, setActiveTab] = useState("create");
  const [filterStatus, setFilterStatus] = useState("");

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showTaskInfoModal, setShowTaskInfoModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  const [vehicleSearchQuery, setVehicleSearchQuery] = useState("");
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false);

  const addressAnchorRef = useRef(null);
  const vehicleAnchorRef = useRef(null);
  

  // API
  const {
    data: tasks = [],
    isLoading: tasksLoading,
    refetch,
  } = useGetAssignedTasksQuery({ status: filterStatus });

  const { data: fieldEmployees = [] } = useGetFieldEmployeesQuery();
  const { data: devicesResponse = [], isLoading: devicesLoading } = useGetAllLiteDevicesQuery();

  const [createTask, { isLoading: creating }] = useCreateTaskMutation();
  const [verifyTask, { isLoading: verifying }] = useVerifyTaskMutation();
  const { data: invData, isLoading: invLoading } = useListItemsQuery({ q: "" });




    // Form state
 const [formData, setFormData] = useState(buildDefaultFormData);


  const inventoryItems = useMemo(() => {
  return Array.isArray(invData?.items) ? invData.items : [];
}, [invData]);

const hardwareTaskType = HARDWARE_TASK_TYPES.has(formData.taskType);
const hidesDeviceAndVehicle = NO_INVENTORY_TASK_TYPES.has(formData.taskType);
const requiresInstallRegistration = INSTALL_TASK_TYPES.has(formData.taskType);
const requiresDeviceUpdate = UPDATE_TASK_TYPES.has(formData.taskType);
const gpsSelection = formData.inventorySelections?.find((selection) => selection.slotKey === "gps") || DEFAULT_SELECTIONS[0];
const simSelection = formData.inventorySelections?.find((selection) => selection.slotKey === "sim") || DEFAULT_SELECTIONS[1];
const bulkSelection = formData.inventorySelections?.find((selection) => selection.slotKey === "bulk_inventory") || DEFAULT_SELECTIONS[2];

const gpsItems = useMemo(
  () => inventoryItems.filter((item) => String(item?.category || "").toLowerCase() === "gps"),
  [inventoryItems]
);
const simItems = useMemo(
  () => inventoryItems.filter((item) => String(item?.category || "").toLowerCase() === "sim"),
  [inventoryItems]
);
const bulkItems = useMemo(
  () =>
    inventoryItems.filter((item) => {
      const category = String(item?.category || "").toLowerCase();
      return category !== "gps" && category !== "sim" && item?.isSerialized === false;
    }),
  [inventoryItems]
);
const selectedGpsItem = useMemo(
  () => gpsItems.find((item) => String(item._id) === String(gpsSelection?.itemId || "")) || null,
  [gpsItems, gpsSelection?.itemId]
);
const selectedSimItem = useMemo(
  () => simItems.find((item) => String(item._id) === String(simSelection?.itemId || "")) || null,
  [simItems, simSelection?.itemId]
);
const selectedBulkItem = useMemo(
  () => bulkItems.find((item) => String(item._id) === String(bulkSelection?.itemId || "")) || null,
  [bulkItems, bulkSelection?.itemId]
);
const { data: gpsUnitsData } = useListItemUnitsQuery(
  { id: gpsSelection?.itemId, status: "in_stock" },
  { skip: !gpsSelection?.itemId }
);
const { data: simUnitsData } = useListItemUnitsQuery(
  { id: simSelection?.itemId, status: "in_stock" },
  { skip: !simSelection?.itemId }
);
const gpsUnits = Array.isArray(gpsUnitsData?.units) ? gpsUnitsData.units : [];
const simUnits = Array.isArray(simUnitsData?.units) ? simUnitsData.units : [];
const selectedGpsUnit = useMemo(
  () => gpsUnits.find((unit) => String(unit._id) === String(gpsSelection?.unitId || "")) || null,
  [gpsUnits, gpsSelection?.unitId]
);
const selectedSimUnit = useMemo(
  () => simUnits.find((unit) => String(unit._id) === String(simSelection?.unitId || "")) || null,
  [simUnits, simSelection?.unitId]
);

const filteredSimItems = useMemo(
  () => simItems.filter((item) => String(item._id) !== String(gpsSelection?.itemId || "")),
  [simItems, gpsSelection?.itemId]
);
  // ✅ Extract devices array safely
  const devices = useMemo(() => {
    if (Array.isArray(devicesResponse)) return devicesResponse;
    if (devicesResponse?.data && Array.isArray(devicesResponse.data)) return devicesResponse.data;
    return [];
  }, [devicesResponse]);

  const [verifyData, setVerifyData] = useState({
    verificationStatus: "approved",
    verificationNotes: "",
  });

  const formatDateTime = (value, fallbackTime = "") => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return fallbackTime ? `${value} ${fallbackTime}` : String(value);
    }
    return `${date.toLocaleDateString()} ${fallbackTime || date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  // ✅ Filter registered vehicles based on search (same logic)
  const filteredVehicles = useMemo(() => {
    if (!vehicleSearchQuery.trim()) return [];
    const query = vehicleSearchQuery.toLowerCase();
    return devices
      .filter(
        (device) =>
          device.name?.toLowerCase().includes(query) ||
          device.imei?.toLowerCase().includes(query) ||
          device.model?.toLowerCase().includes(query) ||
          device.vehicleType?.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [devices, vehicleSearchQuery]);

  // ✅ Google Places suggestions (same feature)
  const fetchAddressSuggestions = async (input) => {
    if (!input || input.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        { input, componentRestrictions: { country: "in" } },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setAddressSuggestions(predictions);
          } else {
            setAddressSuggestions([]);
          }
        }
      );
    } catch (error) {
      console.error("Address suggestion error:", error);
      setAddressSuggestions([]);
    }
  };

  const getPlaceDetails = async (placeId) => {
    try {
      const service = new window.google.maps.places.PlacesService(document.createElement("div"));
      service.getDetails({ placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          setFormData((prev) => ({
            ...prev,
            siteAddress: place.formatted_address,
            siteLatitude: place.geometry.location.lat(),
            siteLongitude: place.geometry.location.lng(),
          }));
          setShowAddressSuggestions(false);
        }
      });
    } catch (error) {
      console.error("Place details error:", error);
    }
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            setFormData((prev) => ({
              ...prev,
              siteAddress: results[0].formatted_address,
            }));
          }
        }
      );
    } catch (error) {
      console.error("Reverse geocode error:", error);
    }
  };

const updateInventorySelectionField = (index, field, value) => {
  setFormData((prev) => ({
    ...prev,
    inventorySelections: prev.inventorySelections.map((selection, selectionIndex) => {
      if (selectionIndex !== index) return selection;
      const nextSelection = {
        ...selection,
        [field]: value,
      };
      if (field === "itemId") {
        nextSelection.unitId = "";
        nextSelection.serial = "";
      }
      if (field === "unitId") {
        nextSelection.serial = "";
      }
      return nextSelection;
    }),
  }));
};

const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target;

  // ✅ Special: Inventory toggle ON/OFF => nested reset
  if (type === "checkbox" && name === "inventoryRequired") {
    setFormData((prev) => ({
      ...prev,
      inventoryRequired: checked,
      inventorySelections: checked
        ? prev.inventorySelections
        : DEFAULT_SELECTIONS.map((selection) => ({
            ...selection,
            handover: { ...selection.handover, ownerLabel: prev.vehicleNumber || "" },
          })),
    }));
    return;
  }

  // ✅ other checkboxes
  if (type === "checkbox") {
    setFormData((prev) => ({ ...prev, [name]: checked }));
    return;
  }

  if (name.startsWith("inventorySelections.")) {
    const [, indexText, section, key, nestedKey] = name.split(".");
    const index = Number(indexText);
    setFormData((prev) => ({
      ...prev,
      inventorySelections: prev.inventorySelections.map((selection, selectionIndex) => {
        if (section === "handover" && key === "ownerLabel") {
          return {
            ...selection,
            handover: {
              ...selection.handover,
              ownerLabel: value,
            },
          };
        }

        if (section === "handover" && key === "ownerType") {
          return {
            ...selection,
            handover: {
              ...selection.handover,
              ownerType: value,
            },
          };
        }

        if (selectionIndex !== index) return selection;

        if (section === "handover") {
          return {
            ...selection,
            handover: {
              ...selection.handover,
              [key]: nestedKey ? { ...selection.handover?.[key], [nestedKey]: value } : value,
            },
          };
        }

        const nextSelection = {
          ...selection,
          [section]: key ? { ...(selection?.[section] || {}), [key]: value } : value,
        };

        if (section === "itemId") {
          nextSelection.unitId = "";
          nextSelection.serial = "";
        }
        if (section === "unitId") {
          nextSelection.serial = "";
        }
        return nextSelection;
      }),
    }));
    return;
  }

  // ✅ normal fields
  setFormData((prev) => {
    if (name === "taskType") {
      const nextTaskType = value;
      const hideInventory = NO_INVENTORY_TASK_TYPES.has(nextTaskType);
      return {
        ...prev,
        taskType: nextTaskType,
        inventoryRequired: hideInventory ? false : prev.inventoryRequired,
      };
    }
    if (name === "vehicleNumber") {
      return {
        ...prev,
        vehicleNumber: value,
        inventorySelections: prev.inventorySelections.map((selection) => ({
          ...selection,
          handover: {
            ...selection.handover,
            ownerLabel:
              selection.handover?.ownerType === "vehicle"
                ? value
                : selection.handover?.ownerLabel || "",
          },
        })),
      };
    }
    return { ...prev, [name]: value };
  });

  // address suggestions
  if (name === "siteAddress") {
    setShowAddressSuggestions(true);
    fetchAddressSuggestions(value);
  }
};


const handleCreateTask = async (e) => {
  e.preventDefault();

  if (!formData.assignedTo) {
    showStatus("warning", "Please select a field employee to assign the task.");
    return;
  }

  const activeSelections =
    formData.inventoryRequired && hardwareTaskType
      ? formData.inventorySelections.filter((selection) => String(selection?.itemId || "").trim())
      : [];

  if (formData.inventoryRequired && hardwareTaskType) {
    if (INSTALL_TASK_TYPES.has(formData.taskType) || formData.taskType === "gps_repair" || formData.taskType === "device_replace") {
      if (!gpsSelection?.itemId || !gpsSelection?.unitId) {
        showStatus("warning", "GPS inventory item aur GPS info select karna zaroori hai.");
        return;
      }
    }
    if (formData.taskType === "sim_change") {
      if (!simSelection?.itemId || !simSelection?.unitId) {
        showStatus("warning", "SIM inventory item aur SIM details select karna zaroori hai.");
        return;
      }
    }
    if (bulkSelection?.itemId) {
      const bulkQty = Math.max(1, Number(bulkSelection?.qty || 1));
      const availableQty = Math.max(0, Number(selectedBulkItem?.quantityOnHand || 0));
      if (bulkQty > availableQty) {
        showStatus("warning", `Selected bulk qty available stock se zyada hai. Available: ${availableQty}.`);
        return;
      }
    }
    for (const selection of activeSelections) {
      if ((selection?.handover?.ownerType || "vehicle") === "vehicle" && !String(selection?.handover?.ownerLabel || formData.vehicleNumber || "").trim()) {
        showStatus("warning", `A vehicle handover label is required for ${selection.slotLabel}.`);
        return;
      }
    }
  }

  try {
    // ✅ Build CLEAN payload - explicit fields only
    const payload = {
      taskType: formData.taskType,
      title: formData.title,
      description: formData.description,
      assignedTo: formData.assignedTo,
      siteAddress: formData.siteAddress,
      siteLatitude: formData.siteLatitude,
      siteLongitude: formData.siteLongitude,
      clientName: formData.clientName,
      clientPhone: formData.clientPhone,
      scheduledDate: formData.scheduledDate,
      scheduledTime: formData.scheduledTime,
      priority: formData.priority,
    };

    if (!hidesDeviceAndVehicle) {
      payload.deviceImei = formData.deviceImei;
      payload.targetDeviceId = formData.targetDeviceId || undefined;
      payload.vehicleNumber = formData.vehicleNumber;
    }
    if (requiresDeviceUpdate) {
      payload.newDeviceImei = formData.newDeviceImei;
    }

    if (formData.inventoryRequired && activeSelections.length) {
      payload.inventoryRequired = true;
      payload.inventorySelections = activeSelections.map((selection) => ({
        slotKey: selection.slotKey,
        slotLabel: selection.slotLabel,
        itemId: selection.itemId,
        unitId: selection.unitId,
        serial:
          selection.slotKey === "gps"
            ? selectedGpsUnit?.serial || selectedGpsUnit?.imei || ""
            : selection.slotKey === "sim"
              ? selectedSimUnit?.serial || selectedSimUnit?.simNumber || selectedSimUnit?.imei || ""
              : "",
        qty: Number(selection.qty || 1),
        handover: {
          ownerType: selection.handover?.ownerType || "vehicle",
          ownerId: selection.handover?.ownerId || undefined,
          ownerLabel: String(selection.handover?.ownerLabel || formData.vehicleNumber || "").trim(),
        },
      }));
    }

    await createTask(payload).unwrap();

    showStatus("success", "Task created and assigned successfully!");

    // reset
    setFormData(buildDefaultFormData());

    setVehicleSearchQuery("");
    refetch();
  } catch (err) {
    console.error("❌ CREATE TASK ERROR:", err);
    const msg =
      err?.data?.msg ||
      (Array.isArray(err?.data?.error) ? err.data.error.join(", ") : "") ||
      "Failed to create task. Check required fields.";
    showStatus("error", msg);
  }
};

  // coordinate -> reverse geocode (same feature)
  useEffect(() => {
    if (formData.siteLatitude && formData.siteLongitude) {
      const lat = parseFloat(formData.siteLatitude);
      const lng = parseFloat(formData.siteLongitude);
      if (!isNaN(lat) && !isNaN(lng)) reverseGeocode(lat, lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.siteLatitude, formData.siteLongitude]);

  useEffect(() => {
    if (!selectedGpsUnit && !selectedSimUnit) return;
    setFormData((prev) => {
      const next = { ...prev };
      const resolvedGpsImei = selectedGpsUnit?.imei || selectedGpsUnit?.serial || "";
      if (resolvedGpsImei) {
        if (INSTALL_TASK_TYPES.has(prev.taskType)) {
          next.deviceImei = resolvedGpsImei;
        } else {
          next.newDeviceImei = resolvedGpsImei;
        }
      }
      return next;
    });
  }, [selectedGpsUnit, selectedSimUnit]);

  // vehicle select (same feature)
  const handleVehicleSelect = (device) => {
    setFormData((prev) => ({
      ...prev,
      vehicleNumber: device.name,
      deviceImei: device.imei || "",
      targetDeviceId: device._id || "",
      inventorySelections: prev.inventorySelections.map((selection) => ({
        ...selection,
        handover: {
          ...selection.handover,
          ownerLabel:
            selection.handover?.ownerType === "vehicle"
              ? device.name
              : selection.handover?.ownerLabel || "",
        },
      })),
    }));
    setVehicleSearchQuery(device.name);
    setShowVehicleSuggestions(false);
  };

  const handleVehicleInputChange = (e) => {
    const value = e.target.value;
    setVehicleSearchQuery(value);
    setFormData((prev) => ({ ...prev, vehicleNumber: value, targetDeviceId: "" }));
    setShowVehicleSuggestions(true);
  };




  const handleVerify = async () => {
    if (!selectedTask) return;
    try {
      await verifyTask({
        taskId: selectedTask._id,
        ...verifyData,
      }).unwrap();

      showStatus("success", `Task for ${selectedTask.assignedTo?.username} verified as ${verifyData.verificationStatus}.`);

      setShowVerifyModal(false);
      setSelectedTask(null);
      refetch();
    } catch (err) {
      showStatus("error", err?.data?.msg || "Verification failed. Please try again.");
    }
  };

  const openVerifyModal = (task) => {
    setSelectedTask(task);
    setVerifyData({ verificationStatus: "approved", verificationNotes: "" });
    setShowVerifyModal(true);
  };

  const openTaskInfoModal = (task) => {
    setSelectedTask(task);
    setShowTaskInfoModal(true);
  };

  const statusChip = (status) => {
    const map = {
      pending: { label: "Pending", tone: "warning" },
      accepted: { label: "Accepted", tone: "info" },
      in_progress: { label: "In Progress", tone: "primary" },
      completed: { label: "Completed", tone: "success" },
      rejected: { label: "Rejected", tone: "error" },
      cancelled: { label: "Cancelled", tone: "default" },
    };
    const s = map[status] || { label: status || "—", tone: "default" };
    return <Chip size="small" label={s.label} color={s.tone} sx={{ fontWeight: 900, borderRadius: 2 }} />;
  };

  const priorityChip = (priority) => {
    const map = {
      low: { label: "Low", tone: "default" },
      medium: { label: "Medium", tone: "info" },
      high: { label: "High", tone: "warning" },
      urgent: { label: "Urgent", tone: "error" },
    };
    const p = map[priority] || map.medium;
    return <Chip size="small" label={p.label} color={p.tone} sx={{ fontWeight: 900, borderRadius: 2 }} />;
  };

  // close suggestion dropdowns on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      const a = addressAnchorRef.current;
      const v = vehicleAnchorRef.current;

      if (a && !a.contains(e.target)) setShowAddressSuggestions(false);
      if (v && !v.contains(e.target)) setShowVehicleSuggestions(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fieldEmployeeOptions = fieldEmployees?.data || fieldEmployees || [];
  const selectedTaskType = TASK_TYPE_OPTIONS.find((option) => option.value === formData.taskType) || TASK_TYPE_OPTIONS[0];
  const selectedFieldEmployee =
    fieldEmployeeOptions.find((emp) => String(emp._id) === String(formData.assignedTo || "")) || null;
  const selectedPriority =
    PRIORITY_OPTIONS.find((option) => option.value === formData.priority) || PRIORITY_OPTIONS[1];
  const deviceImeiLabel =
    formData.taskType === "gps_repair"
      ? "Replace Device IMEI"
      : formData.taskType === "sim_change"
      ? "Current Device IMEI"
      : formData.taskType === "device_replace"
      ? "Replace Device IMEI"
      : formData.taskType === "device_update"
      ? "Current Device IMEI"
      : "Device IMEI";
  const newDeviceImeiLabel =
    formData.taskType === "gps_repair"
      ? "Add Device IMEI Number"
      : formData.taskType === "sim_change"
      ? "SIM Change GPS"
      : formData.taskType === "device_replace"
      ? "New Device IMEI Number"
      : "Updated Device IMEI";
  const taskTypeMenuSx = {
    borderRadius: 3,
    mt: 1,
    "& .MuiMenuItem-root": {
      alignItems: "flex-start",
      py: 1.25,
    },
  };

  return (
    <Box sx={{ p: { xs: 1.2, md: 2.5 } }}>
      {/* Header */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.6, md: 2.2 },
          borderRadius: 4,
          borderColor: alpha(theme.palette.divider, 0.9),
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: 1000, fontSize: { xs: 20, md: 26 } }}>
          Task Management
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Assign and manage field tasks
        </Typography>
      </Paper>

      {/* Tabs */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          borderColor: alpha(theme.palette.divider, 0.9),
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isSmDown ? "fullWidth" : "standard"}
          sx={{ px: 1 }}
        >
          <Tab value="create" label="Create Task" />
          <Tab value="list" label="All Tasks" />
        </Tabs>
        <Divider />
      </Paper>

      {/* CREATE TAB */}
      {activeTab === "create" && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            borderColor: alpha(theme.palette.divider, 0.9),
            p: { xs: 1.4, md: 2.2 },
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box component="form" onSubmit={handleCreateTask}>
            <Stack spacing={2}>
              <Box sx={{ px: { xs: 0.2, md: 0.4 } }}>
                <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 22 } }}>
                  Create Field Task
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.4 }}>
                  Fill the task details, assign a field employee, and schedule the visit properly.
                </Typography>
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.4, md: 2 },
                  borderRadius: 3.5,
                  borderColor: alpha(theme.palette.divider, 0.9),
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Stack spacing={1.6} sx={{ mb: 1.6 }}>
                  <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Task Details</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose the task category, assignee, and write a clear task summary.
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Task Type"
                      name="taskType"
                      value={formData.taskType}
                      onChange={handleInputChange}
                      required
                      helperText={selectedTaskType?.helper || "Select task type"}
                      SelectProps={{
                        MenuProps: { PaperProps: { sx: taskTypeMenuSx } },
                      }}
                    >
                      {TASK_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box>
                            <Typography sx={{ fontWeight: 800 }}>{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.helper}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Assign To"
                      name="assignedTo"
                      value={formData.assignedTo}
                      onChange={handleInputChange}
                      required
                      helperText={
                        selectedFieldEmployee
                          ? `${selectedFieldEmployee.username}${selectedFieldEmployee.mobileNo ? ` • ${selectedFieldEmployee.mobileNo}` : ""}`
                          : "Select the field employee who will handle this task"
                      }
                      SelectProps={{
                        MenuProps: { PaperProps: { sx: taskTypeMenuSx } },
                      }}
                    >
                      <MenuItem value="">Select Field Employee</MenuItem>
                      {fieldEmployeeOptions.map((emp) => (
                        <MenuItem key={emp._id} value={emp._id}>
                          <Box>
                            <Typography sx={{ fontWeight: 800 }}>{emp.username}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {emp.mobileNo || "Mobile not available"}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      helperText={`Selected priority: ${selectedPriority.label}`}
                      SelectProps={{
                        MenuProps: { PaperProps: { sx: taskTypeMenuSx } },
                      }}
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Task Title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter a clear task title"
                      required
                    />
                  </Grid>

                  {!hidesDeviceAndVehicle && (
                    <Grid item xs={12} md={requiresDeviceUpdate ? 3 : 6}>
                      <TextField
                        fullWidth
                        label={deviceImeiLabel}
                        name="deviceImei"
                        value={formData.deviceImei}
                        onChange={handleInputChange}
                        placeholder="Vehicle/device se linked IMEI"
                        InputProps={{
                          readOnly:
                            !!vehicleSearchQuery && filteredVehicles.some((d) => d.name === vehicleSearchQuery),
                        }}
                        helperText={formData.deviceImei ? "Current device IMEI" : "Optional if vehicle is not selected yet"}
                      />
                    </Grid>
                  )}

                  {!hidesDeviceAndVehicle && requiresDeviceUpdate && (
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={newDeviceImeiLabel}
                        name="newDeviceImei"
                        value={formData.newDeviceImei}
                        onChange={handleInputChange}
                        placeholder="Selected GPS inventory se auto-filled"
                        helperText={formData.newDeviceImei ? "Selected GPS inventory IMEI" : "This will auto-fill from the selected GPS inventory"}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Write task instructions, issue details, or visit notes"
                      multiline
                      minRows={4}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.4, md: 2 },
                  borderRadius: 3.5,
                  borderColor: alpha(theme.palette.divider, 0.9),
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Stack spacing={1.6} sx={{ mb: 1.6 }}>
                  <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Location And Client</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Search the vehicle and exact site address so the field employee gets proper task context.
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  {!hidesDeviceAndVehicle && (
                    <Grid item xs={12} md={6}>
                      <Box ref={vehicleAnchorRef} sx={{ position: "relative" }}>
                        <TextField
                          fullWidth
                          label="Vehicle Number"
                          value={vehicleSearchQuery}
                          onChange={handleVehicleInputChange}
                          onFocus={() => setShowVehicleSuggestions(true)}
                          placeholder="Search registered vehicle..."
                          helperText={formData.vehicleNumber ? `Selected vehicle: ${formData.vehicleNumber}` : "Optional"}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search size={18} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {showVehicleSuggestions && filteredVehicles.length > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{
                              position: "absolute",
                              zIndex: 20,
                              left: 0,
                              right: 0,
                              mt: 1,
                              borderRadius: 3,
                              overflow: "hidden",
                              borderColor: alpha(theme.palette.divider, 0.9),
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: `0 14px 36px ${alpha(theme.palette.common.black, 0.1)}`,
                            }}
                          >
                            {filteredVehicles.map((device) => (
                              <Box
                                key={device._id}
                                onClick={() => handleVehicleSelect(device)}
                                sx={{
                                  px: 1.6,
                                  py: 1.2,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                                }}
                              >
                                <Typography sx={{ fontWeight: 900 }}>{device.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {device.model || "—"} • {device.vehicleType || "N/A"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  IMEI: {device.imei || "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Box ref={addressAnchorRef} sx={{ position: "relative" }}>
                      <TextField
                        fullWidth
                        label="Site Address"
                        name="siteAddress"
                        value={formData.siteAddress}
                        onChange={handleInputChange}
                        onFocus={() => setShowAddressSuggestions(true)}
                        placeholder="Start typing address..."
                        required
                        helperText="Search and select the exact site location"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MapPin size={18} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {showAddressSuggestions && addressSuggestions.length > 0 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            position: "absolute",
                            zIndex: 20,
                            left: 0,
                            right: 0,
                            mt: 1,
                            borderRadius: 3,
                            overflow: "hidden",
                            borderColor: alpha(theme.palette.divider, 0.9),
                            maxHeight: 280,
                            overflowY: "auto",
                            boxShadow: `0 14px 36px ${alpha(theme.palette.common.black, 0.1)}`,
                          }}
                        >
                          {addressSuggestions.map((s) => (
                            <Box
                              key={s.place_id}
                              onClick={() => getPlaceDetails(s.place_id)}
                              sx={{
                                px: 1.6,
                                py: 1.2,
                                cursor: "pointer",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {s.description}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Client Name"
                      name="clientName"
                      value={formData.clientName}
                      onChange={handleInputChange}
                      placeholder="Client or contact person name"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Client Phone"
                      name="clientPhone"
                      value={formData.clientPhone}
                      onChange={handleInputChange}
                      placeholder="Client phone number"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Latitude"
                      type="number"
                      inputProps={{ step: "any" }}
                      name="siteLatitude"
                      value={formData.siteLatitude}
                      onChange={handleInputChange}
                      placeholder="28.6139"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Longitude"
                      type="number"
                      inputProps={{ step: "any" }}
                      name="siteLongitude"
                      value={formData.siteLongitude}
                      onChange={handleInputChange}
                      placeholder="77.2090"
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.4, md: 2 },
                  borderRadius: 3.5,
                  borderColor: alpha(theme.palette.divider, 0.9),
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Stack spacing={1.6} sx={{ mb: 1.6 }}>
                  <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Schedule</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set the visit date and time so the assignee knows when to report.
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Scheduled Date"
                      type="date"
                      name="scheduledDate"
                      value={formData.scheduledDate}
                      onChange={handleInputChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Scheduled Time"
                      type="time"
                      name="scheduledTime"
                      value={formData.scheduledTime}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.4, md: 2 },
                  borderRadius: 3.5,
                  borderColor: alpha(theme.palette.divider, 0.9),
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Stack spacing={1.6}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Inventory</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable this only when some item needs to be issued for the task.
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!formData.inventoryRequired}
                          onChange={handleInputChange}
                          name="inventoryRequired"
                        />
                      }
                      label="Inventory Required"
                      sx={{ m: 0, "& .MuiFormControlLabel-label": { fontWeight: 800 } }}
                    />
                  </Stack>

                  {formData.inventoryRequired && hardwareTaskType && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          fullWidth
                          options={gpsItems}
                          value={selectedGpsItem}
                          onChange={(_, value) => updateInventorySelectionField(0, "itemId", value?._id || "")}
                          getOptionLabel={getInventoryItemOptionLabel}
                          isOptionEqualToValue={(option, value) => String(option?._id || "") === String(value?._id || "")}
                          loading={invLoading}
                          renderOption={(props, it) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography sx={{ fontWeight: 800 }}>{it.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {it.isSerialized ? "Serialized GPS" : "Bulk GPS"}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="GPS Inventory"
                              helperText={
                                selectedGpsItem
                                  ? `${selectedGpsItem.name} • ${selectedGpsItem.isSerialized ? "Serialized" : "Bulk"}`
                                  : "Search and select the GPS inventory item"
                              }
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          fullWidth
                          options={gpsUnits}
                          value={selectedGpsUnit}
                          onChange={(_, value) => updateInventorySelectionField(0, "unitId", value?._id || "")}
                          getOptionLabel={getGpsUnitOptionLabel}
                          isOptionEqualToValue={(option, value) => String(option?._id || "") === String(value?._id || "")}
                          disabled={!gpsSelection?.itemId}
                          renderOption={(props, unit) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography sx={{ fontWeight: 800 }}>{formatUnitOptionLabel(unit)}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {unit.provider || unit.modelType || unit.status}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="GPS Info"
                              helperText={selectedGpsUnit ? formatUnitOptionLabel(selectedGpsUnit) : "Search and select GPS details from the selected inventory"}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          fullWidth
                          options={filteredSimItems}
                          value={selectedSimItem}
                          onChange={(_, value) => updateInventorySelectionField(1, "itemId", value?._id || "")}
                          getOptionLabel={getInventoryItemOptionLabel}
                          isOptionEqualToValue={(option, value) => String(option?._id || "") === String(value?._id || "")}
                          renderOption={(props, it) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography sx={{ fontWeight: 800 }}>{it.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {it.provider || "SIM inventory"}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="SIM Inventory"
                              helperText={
                                selectedSimItem
                                  ? `${selectedSimItem.name} • ${selectedSimItem.isSerialized ? "Serialized" : "Bulk"}`
                                  : "Search and select the SIM inventory item if needed"
                              }
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          fullWidth
                          options={simUnits}
                          value={selectedSimUnit}
                          onChange={(_, value) => updateInventorySelectionField(1, "unitId", value?._id || "")}
                          getOptionLabel={getSimUnitOptionLabel}
                          isOptionEqualToValue={(option, value) => String(option?._id || "") === String(value?._id || "")}
                          disabled={!simSelection?.itemId}
                          renderOption={(props, unit) => (
                            <Box component="li" {...props}>
                              <Box sx={{ py: 0.25 }}>
                                <Typography sx={{ fontWeight: 800 }}>{unit.simNumber || unit.serial || unit.imei || "SIM unit"}</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  IMSI: {unit.imsiNumber || "-"}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Provider: {unit.provider || "-"}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  IoT SIM: {unit.iotSimNumber || "-"}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="SIM Details"
                              helperText={selectedSimUnit ? formatUnitOptionLabel(selectedSimUnit) : "Search and select SIM details from the selected inventory"}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          fullWidth
                          options={bulkItems}
                          value={selectedBulkItem}
                          onChange={(_, value) => updateInventorySelectionField(2, "itemId", value?._id || "")}
                          getOptionLabel={getInventoryItemOptionLabel}
                          isOptionEqualToValue={(option, value) => String(option?._id || "") === String(value?._id || "")}
                          renderOption={(props, it) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography sx={{ fontWeight: 800 }}>{it.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {String(it.category || "general").replaceAll("_", " ")} • In stock: {Number(it.quantityOnHand || 0)}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Other Inventory / Bulk Item"
                              helperText={
                                selectedBulkItem
                                  ? `${selectedBulkItem.name} • Available stock: ${Number(selectedBulkItem.quantityOnHand || 0)}`
                                  : "Optional: battery, tyre, tool, fastag, general item, etc."
                              }
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Qty for Task"
                          value={bulkSelection?.qty || 1}
                          onChange={(e) => updateInventorySelectionField(2, "qty", Math.max(1, Number(e.target.value || 1)))}
                          inputProps={{ min: 1 }}
                          disabled={!bulkSelection?.itemId}
                          helperText={
                            selectedBulkItem
                              ? `Task complete hone par ${bulkSelection?.qty || 1} qty is stock me se deduct / assign hogi. Remaining stock: ${Math.max(0, Number(selectedBulkItem.quantityOnHand || 0) - Math.max(1, Number(bulkSelection?.qty || 1)))}`
                              : "Pehle bulk item select karo, phir qty do"
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label="Handover To"
                          name="inventorySelections.0.handover.ownerType"
                          value={gpsSelection?.handover?.ownerType || "vehicle"}
                          onChange={handleInputChange}
                          SelectProps={{
                            MenuProps: { PaperProps: { sx: taskTypeMenuSx } },
                          }}
                        >
                          <MenuItem value="vehicle">Vehicle</MenuItem>
                          <MenuItem value="warehouse">Warehouse</MenuItem>
                          <MenuItem value="dealer">Dealer</MenuItem>
                          <MenuItem value="employee">Employee</MenuItem>
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="device">Device</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          label={(gpsSelection?.handover?.ownerType || "vehicle") === "vehicle" ? "Vehicle Number (Owner Label)" : "Owner Label"}
                          name="inventorySelections.0.handover.ownerLabel"
                          value={gpsSelection?.handover?.ownerLabel || formData.vehicleNumber || ""}
                          onChange={handleInputChange}
                          placeholder="e.g. DL01AB1234 / Rahul / Dealer A"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 3 }}>
                          The selected inventory snapshot is saved with the task so the field employee sees the same GPS and SIM details during registration or update.
                        </Alert>
                      </Grid>
                    </Grid>
                  )}
                </Stack>
              </Paper>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ px: { xs: 0.2, md: 0.4 } }}
              >
                <Typography variant="body2" color="text.secondary">
                  Make sure assignee, address, and schedule are correct before creating the task.
                </Typography>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={creating}
                  sx={{ fontWeight: 1000, borderRadius: 2.5, px: 3.2, minWidth: 180 }}
                >
                  {creating ? "Creating..." : "Create Task"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* LIST TAB */}
      {activeTab === "list" && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            borderColor: alpha(theme.palette.divider, 0.9),
            overflow: "hidden",
          }}
        >
          {/* Filters bar */}
          <Box sx={{ p: { xs: 1.4, md: 2 }, bgcolor: alpha(theme.palette.background.paper, 0.98) }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }} justifyContent="space-between">
              <TextField
                select
                size="small"
                label="Status Filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 220 } }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>

              <Button
                variant="outlined"
                onClick={refetch}
                startIcon={<RefreshCw size={18} />}
                sx={{ fontWeight: 900, borderRadius: 2.5 }}
              >
                Refresh
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Content */}
          {tasksLoading ? (
            <Box sx={{ p: 5, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : tasks.length === 0 ? (
            <Box sx={{ p: 5, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>No tasks found</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.7 }}>
                Try changing the status filter.
              </Typography>
            </Box>
          ) : (
            <ScrollableTableContainer showScrollButtons={false} sx={{ maxHeight: "calc(100vh - 290px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 1000, minWidth: 220 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Assigned To</TableCell>
                    <TableCell sx={{ fontWeight: 1000, minWidth: 190 }}>Assigned By</TableCell>
                    <TableCell sx={{ fontWeight: 1000, minWidth: 260 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Schedule</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Inventory</TableCell>
                    <TableCell sx={{ fontWeight: 1000, minWidth: 200 }}>Vehicle</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task._id} hover>
                      <TableCell>{statusChip(task.status)}</TableCell>
                      <TableCell>{priorityChip(task.priority)}</TableCell>

                      <TableCell sx={{ fontWeight: 900 }}>
                        <Stack spacing={0.35}>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            {task.title}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {(task.taskType || "").replace("_", " ")}
                      </TableCell>

                      <TableCell>
                        {task.assignedTo?.username || "N/A"}
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.35}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {task.assignedBy?.username || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(task.assignedAt || task.createdAt)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={task.siteAddress || ""}>
                          <Typography variant="body2" sx={{ maxWidth: 340 }} noWrap>
                            {task.siteAddress ? `${task.siteAddress.slice(0, 60)}${task.siteAddress.length > 60 ? "…" : ""}` : "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.35}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {task.scheduledDate ? new Date(task.scheduledDate).toLocaleDateString() : "-"}
                          </Typography>
                          {task.scheduledTime && (
                            <Typography variant="caption" color="text.secondary">
                              At: {task.scheduledTime}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Added: {formatDateTime(task.createdAt)}
                          </Typography>
                        </Stack>
                      </TableCell>
<TableCell>
  {task.inventoryRequired ? (
    <Stack spacing={0.4}>
      <Chip
        size="small"
        label={formatInventoryStatusLabel(task.inventoryStatus)}
        color={
          task.inventoryStatus === "handed_over"
            ? "success"
          : task.inventoryStatus === "issued"
            ? "info"
          : task.inventoryStatus === "returned"
            ? "warning"
          : task.inventoryStatus === "failed" || task.inventoryStatus === "issue_failed" || task.inventoryStatus === "handover_failed" || task.inventoryStatus === "return_failed"
            ? "error"
          : "default"
        }
        sx={{ fontWeight: 900, borderRadius: 2, textTransform: "capitalize" }}
      />

      {(task.inventorySelections || []).length ? (
        task.inventorySelections.map((selection) => (
          <Typography
            key={selection._id || `${selection.slotKey}-${selection.itemId}`}
            variant="caption"
            color="text.secondary"
          >
            {(selection.slotLabel || selection.itemName || "Inventory")}: {selection.displayLabel || selection.itemName || selection.serial || selection.qty}
            {!selection?.isSerialized && Number(selection?.qty || 0) > 0 ? ` • Qty ${selection.qty}` : ""}
          </Typography>
        ))
      ) : task.inventoryIssue?.itemName ? (
        <Typography variant="caption" color="text.primary" sx={{ fontWeight: 700 }}>
          {task.inventoryIssue.itemName}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Item not issued yet
        </Typography>
      )}

      {task.inventoryHandover?.ownerLabel ? (
        <Typography variant="caption" color="text.secondary">
          For: {task.inventoryHandover.ownerLabel}
        </Typography>
      ) : null}

      <Typography variant="caption" color="text.secondary">
        Selected by: {task.assignedBy?.username || "N/A"}
      </Typography>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">—</Typography>
  )}
</TableCell>



                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {task.vehicleNumber || "-"}
                        </Typography>
                        {task.deviceImei && (
                          <Typography variant="caption" color="text.secondary">
                            IMEI: {task.deviceImei}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {task.status === "completed" && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openTaskInfoModal(task)}
                              sx={{ fontWeight: 900, borderRadius: 2 }}
                            >
                              View Information
                            </Button>
                          )}
                          {task.status === "completed" && task.verificationStatus === "pending" ? (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => openVerifyModal(task)}
                              sx={{ fontWeight: 900, borderRadius: 2 }}
                            >
                              Verify
                            </Button>
                          ) : task.verificationStatus && task.verificationStatus !== "pending" ? (
                            <Chip
                              size="small"
                              label={task.verificationStatus}
                              color={task.verificationStatus === "approved" ? "success" : "error"}
                              sx={{ fontWeight: 900, borderRadius: 2, textTransform: "capitalize" }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          )}
        </Paper>
      )}

      {/* VERIFY MODAL */}
      <Dialog
        open={showTaskInfoModal && Boolean(selectedTask)}
        onClose={() => setShowTaskInfoModal(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ pb: 1.2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography sx={{ fontWeight: 1000 }}>Task Information</Typography>
            <IconButton onClick={() => setShowTaskInfoModal(false)}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>
        {selectedTask && (
          <>
            <DialogContent sx={{ pt: 1 }}>
              <Stack spacing={1.5}>
                {(() => {
                  const {
                    taskSummaryRows,
                    vehicleInfoRows,
                    simInfoRows,
                    siteInfoRows,
                    inventoryMetaRows,
                    notesRows,
                  } = buildTaskInfoRows(selectedTask);

                  return (
                    <>
                      <Box>
                        <Typography sx={{ fontWeight: 1000, fontSize: 22 }}>
                          {selectedTask.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                          {getTaskInfoValue(selectedTask.taskType).replaceAll("_", " ")} • {selectedTask.assignedTo?.username || "—"}
                        </Typography>
                      </Box>

                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={6}>
                          <InfoTableSection title="Task Summary" rows={taskSummaryRows} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <InfoTableSection title="Vehicle / Device Information" rows={vehicleInfoRows} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <InfoTableSection title="SIM / Connectivity Details" rows={simInfoRows} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <InfoTableSection title="Site / Client Details" rows={siteInfoRows} />
                        </Grid>
                        <Grid item xs={12}>
                          <InfoTableSection title="Inventory Status" rows={inventoryMetaRows} />
                        </Grid>
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                            <Typography sx={{ fontWeight: 900, mb: 1 }}>Inventory Snapshot</Typography>
                            <Stack spacing={1}>
                              {(selectedTask.inventorySelections || []).length ? (
                                selectedTask.inventorySelections.map((selection) => (
                                  <Box key={selection._id || `${selection.slotKey}-${selection.itemId}`}>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                      {selection.slotLabel || selection.slotKey || "Inventory"}
                                      {selection?.handover?.ownerLabel ? ` • For ${selection.handover.ownerLabel}` : ""}
                                    </Typography>
                                    <Box
                                      sx={{
                                        mt: 0.75,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                      }}
                                    >
                                      {getSelectionSnapshotRows(selection).map((row, index) => (
                                        <Stack
                                          key={`${selection.slotKey}-${row.label}`}
                                          direction={{ xs: "column", md: "row" }}
                                          spacing={1}
                                          sx={{
                                            px: 1.2,
                                            py: 0.9,
                                            borderTop: index === 0 ? "none" : "1px solid",
                                            borderColor: "divider",
                                            bgcolor: index % 2 ? "action.hover" : "transparent",
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: 800, minWidth: { xs: "100%", md: 180 } }}>
                                            {row.label}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                            {row.value}
                                          </Typography>
                                        </Stack>
                                      ))}
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No inventory snapshot saved
                                </Typography>
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                        <Grid item xs={12}>
                          <InfoTableSection title="Notes / Remarks" rows={notesRows} />
                        </Grid>
                      </Grid>
                    </>
                  );
                })()}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button variant="outlined" onClick={() => setShowTaskInfoModal(false)} sx={{ fontWeight: 900, borderRadius: 2 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* VERIFY MODAL */}
      <Dialog
        open={showVerifyModal && Boolean(selectedTask)}
        onClose={() => setShowVerifyModal(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
      >
        <DialogTitle sx={{ pb: 1.2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography sx={{ fontWeight: 1000 }}>Verify Task</Typography>
            <IconButton onClick={() => setShowVerifyModal(false)}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        {selectedTask && (
          <>
            <DialogContent sx={{ pt: 1 }}>
              <Stack spacing={1.2}>
                <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>{selectedTask.title}</Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Field Employee:</strong> {selectedTask.assignedTo?.username || "—"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Completed At:</strong>{" "}
                  {selectedTask.completedAt ? new Date(selectedTask.completedAt).toLocaleString() : "—"}
                </Typography>

                {selectedTask.fieldNotes && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                    <Typography sx={{ fontWeight: 900 }}>Field Notes</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {selectedTask.fieldNotes}
                    </Typography>
                  </Paper>
                )}

                {selectedTask.photos?.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 900, mb: 1 }}>Photos</Typography>
                    <Grid container spacing={1.2}>
                      {selectedTask.photos.map((photo, idx) => (
                        <Grid key={idx} item xs={6} sm={4} md={3}>
                          <Paper
                            variant="outlined"
                            sx={{
                              borderRadius: 3,
                              overflow: "hidden",
                              borderColor: alpha(theme.palette.divider, 0.9),
                            }}
                          >
                            <Box
                              component="img"
                              src={photo.url}
                              alt={photo.type}
                              sx={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                            />
                            <Box sx={{ p: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {photo.type}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      select
                      fullWidth
                      label="Verification Status"
                      value={verifyData.verificationStatus}
                      onChange={(e) =>
                        setVerifyData((prev) => ({ ...prev, verificationStatus: e.target.value }))
                      }
                    >
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={verifyData.verificationNotes}
                      onChange={(e) =>
                        setVerifyData((prev) => ({ ...prev, verificationNotes: e.target.value }))
                      }
                      placeholder="Add verification notes..."
                      multiline
                      minRows={3}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: "100%" }} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setShowVerifyModal(false)} sx={{ fontWeight: 900, borderRadius: 2 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleVerify}
                  disabled={verifying}
                  sx={{ fontWeight: 1000, borderRadius: 2 }}
                >
                  {verifying ? "Verifying..." : "Submit Verification"}
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TaskManagement;
